import { useState, useEffect, useCallback, useMemo } from "react";
import {
    Gift, Loader2, IndianRupee, Package, Percent, Search, CheckCircle2, XCircle,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import {
    REFERRAL_AWARDABLE_URL, REFERRAL_REWARDS_MANAGE_URL, REFERRAL_REWARDS_URL,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { formatDate } from "../../utils/datetime";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const TYPES = [
    { value: "money", label: "Money", Icon: IndianRupee },
    { value: "amc_discount", label: "AMC discount", Icon: Percent },
    { value: "free_modules", label: "Free modules", Icon: Package },
];

const BENEFICIARIES = [
    { value: "organization", label: "The hospital" },
    { value: "individual", label: "The person who referred" },
];

const STATUSES = [
    { value: "all", label: "All" },
    { value: "draft", label: "Draft" },
    { value: "approved", label: "Approved" },
    { value: "settled", label: "Settled" },
    { value: "cancelled", label: "Cancelled" },
];

const STATUS_CLS = {
    draft: "bg-gray-100 text-gray-600 border-gray-200",
    approved: "bg-blue-100 text-blue-800 border-blue-200",
    settled: "bg-green-100 text-green-800 border-green-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
};

const input = "rounded-lg border border-gray-300 px-3 py-2 text-sm " +
    "focus:outline-none focus:ring-2 focus:ring-blue-500";

/** Decide what converted referrals earned, and track handing it over.
 *
 *  The two halves are one decision seen from both ends: which referrals have
 *  earned something, and what has been granted so far.
 *
 *  Nothing pays out automatically. The rates in Settings only pre-fill a
 *  suggestion — what a set of referrals is worth, and whether it is money, an
 *  AMC credit or free modules, is a commercial judgement somebody records.
 *
 *  Awards stage draft → approved → settled because those are three acts,
 *  often by three people. Collapsing them would make "what have we promised
 *  but not yet given" impossible to ask.
 */
export default function ReferralRewards() {
    const { fetch, create, patch } = useApiMethods();

    const [awardable, setAwardable] = useState([]);
    const [suggested, setSuggested] = useState({});
    const [loadingAwardable, setLoadingAwardable] = useState(true);
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [selected, setSelected] = useState([]);

    const [rewards, setRewards] = useState([]);
    const [totals, setTotals] = useState(null);
    const [loadingRewards, setLoadingRewards] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");

    const [beneficiary, setBeneficiary] = useState("organization");
    const [benefitType, setBenefitType] = useState("money");
    const [percent, setPercent] = useState("");
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    const [settling, setSettling] = useState(null);
    const [settleNote, setSettleNote] = useState("");

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    const loadAwardable = useCallback(() => {
        setLoadingAwardable(true);
        fetch(REFERRAL_AWARDABLE_URL, debounced.trim() ? { search: debounced.trim() } : {})
            .then(res => {
                if (res?.status === "success") {
                    setAwardable(res.data || []);
                    setSuggested(res.suggested || {});
                } else {
                    ToastMsg("error", res?.message || "Could not load referrals");
                }
            })
            .catch(() => ToastMsg("error", "Could not load referrals"))
            .finally(() => setLoadingAwardable(false));
    }, [debounced]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadRewards = useCallback(() => {
        setLoadingRewards(true);
        const filters = {};
        if (statusFilter !== "all") filters.status = statusFilter;
        if (typeFilter !== "all") filters.benefit_type = typeFilter;
        fetch(REFERRAL_REWARDS_MANAGE_URL, filters)
            .then(res => {
                if (res?.status === "success") {
                    setRewards(res.data || []);
                    setTotals(res.totals || null);
                } else {
                    ToastMsg("error", res?.message || "Could not load rewards");
                }
            })
            .catch(() => ToastMsg("error", "Could not load rewards"))
            .finally(() => setLoadingRewards(false));
    }, [statusFilter, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadAwardable(); }, [loadAwardable]);
    useEffect(() => { loadRewards(); }, [loadRewards]);

    const alreadyAwarded = (row) => (row.awarded_to || []).includes(beneficiary);

    const toggle = (id) => setSelected(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const selectedRows = useMemo(
        () => awardable.filter(r => selected.includes(r.id)),
        [awardable, selected],
    );

    // One award belongs to one referring hospital — the server refuses a mixed
    // set, so saying so here beats a rejection after the form is filled in.
    const referrers = useMemo(
        () => [...new Set(selectedRows.map(r => r.referrer_customer_id))],
        [selectedRows],
    );
    const mixedReferrers = referrers.length > 1;
    const unmappedReferrer = referrers.includes(null) || referrers.includes(undefined);

    const basis = useMemo(
        () => selectedRows.reduce((s, r) => s + Number(r.won_amount || 0), 0),
        [selectedRows],
    );

    const suggestedPct = beneficiary === "individual"
        ? suggested.individual_benefit_percent
        : suggested.org_benefit_percent;

    const computed = useMemo(() => {
        const p = Number(percent);
        if (!percent || Number.isNaN(p)) return null;
        return (basis * p) / 100;
    }, [basis, percent]);

    const effective = amount !== "" ? Number(amount) : (computed ?? 0);

    const canAward = selected.length > 0 && description.trim()
        && !mixedReferrers && !unmappedReferrer && !saving;

    const award = () => {
        setSaving(true);
        create(REFERRAL_REWARDS_MANAGE_URL, {
            referral_ids: selected,
            beneficiary_type: beneficiary,
            benefit_type: benefitType,
            percent: percent === "" ? null : percent,
            amount: amount === "" ? null : amount,
            description: description.trim(),
        })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message || "Reward recorded");
                    setSelected([]); setPercent(""); setAmount(""); setDescription("");
                    loadAwardable(); loadRewards();
                } else {
                    ToastMsg("error", res?.message || "Could not record reward");
                }
            })
            .catch(err => ToastMsg("error",
                err?.response?.data?.message || "Could not record reward"))
            .finally(() => setSaving(false));
    };

    const move = (id, status, note) => {
        const body = { status };
        if (note) body.settlement_note = note;
        patch(`${REFERRAL_REWARDS_URL}${id}/`, body)
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message || "Updated");
                    setSettling(null); setSettleNote("");
                    loadAwardable(); loadRewards();
                } else {
                    ToastMsg("error", res?.message || "Could not update reward");
                }
            })
            .catch(err => ToastMsg("error",
                err?.response?.data?.message || "Could not update reward"));
    };

    return (
        <div className="p-5 space-y-5">
            <div>
                <h1 className="text-xl font-semibold text-gray-900">Referral Rewards</h1>
                <p className="text-sm text-gray-500 mt-1">
                    What converted referrals earned. The rates in Settings suggest a
                    figure; the award is your decision.
                </p>
            </div>

            {totals && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
                    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                        <p className="text-xs text-gray-500">Awarded</p>
                        <p className="text-lg font-semibold text-gray-900">{money(totals.awarded)}</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                        <p className="text-xs text-gray-500">Not yet settled</p>
                        <p className={cn("text-lg font-semibold",
                            Number(totals.outstanding) > 0 ? "text-amber-700" : "text-gray-900")}>
                            {money(totals.outstanding)}
                        </p>
                    </div>
                </div>
            )}

            {/* ── Awardable ───────────────────────────────────────────── */}
            <section className="rounded-lg border border-gray-200 bg-white">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
                    <h2 className="text-sm font-semibold text-gray-900">Onboarded referrals</h2>
                    <span className="text-xs text-gray-500">
                        Nothing is earned before a referred hospital onboards.
                    </span>
                    <div className="ml-auto relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Code, hospital, referrer"
                            className={cn(input, "pl-8 w-64")}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600">
                            <tr>
                                <th className="w-10 px-4 py-2.5" />
                                <th className="text-left font-medium px-4 py-2.5">Referral</th>
                                <th className="text-left font-medium px-4 py-2.5">Hospital won</th>
                                <th className="text-left font-medium px-4 py-2.5">Referred by</th>
                                <th className="text-right font-medium px-4 py-2.5">Won at</th>
                                <th className="text-left font-medium px-4 py-2.5">Already awarded</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loadingAwardable && (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…
                                </td></tr>
                            )}

                            {!loadingAwardable && awardable.length === 0 && (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                    No onboarded referrals yet.
                                </td></tr>
                            )}

                            {!loadingAwardable && awardable.map(r => {
                                const taken = alreadyAwarded(r);
                                return (
                                    <tr key={r.id} className={taken ? "opacity-60" : "hover:bg-gray-50"}>
                                        <td className="px-4 py-2.5">
                                            <input
                                                type="checkbox"
                                                disabled={taken}
                                                checked={selected.includes(r.id)}
                                                onChange={() => toggle(r.id)}
                                            />
                                        </td>
                                        <td className="px-4 py-2.5 font-mono text-xs">{r.referral_code}</td>
                                        <td className="px-4 py-2.5">{r.hospital_name || "—"}</td>
                                        <td className="px-4 py-2.5">
                                            <div>{r.referrer_name || "—"}</div>
                                            <div className="text-xs text-gray-500">
                                                {r.submitted_by_name}
                                                {!r.referrer_customer_id && (
                                                    <span className="text-amber-700"> · not mapped</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-right">{money(r.won_amount)}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">
                                            {(r.awarded_to || []).join(", ") || "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* ── Award form ──────────────────────────────────────────── */}
            {selected.length > 0 && (
                <section className="rounded-lg border border-blue-200 bg-blue-50/40">
                    <div className="px-4 py-3 border-b border-blue-100">
                        <h2 className="text-sm font-semibold text-gray-900">
                            Award for {selected.length} referral(s)
                        </h2>
                        <p className="text-xs text-gray-600 mt-0.5">
                            Combined won value {money(basis)}.
                        </p>
                    </div>

                    {(mixedReferrers || unmappedReferrer) && (
                        <p className="mx-4 mt-3 rounded-lg border border-amber-200 bg-amber-50
                            px-3 py-2 text-xs text-amber-900">
                            {mixedReferrers
                                ? "These referrals come from different hospitals. An award belongs to one, so that “what did this customer earn” stays answerable."
                                : "The referring hospital is not mapped to an eMedHub organisation yet, so there is no customer to award."}
                        </p>
                    )}

                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="text-xs font-medium text-gray-600">Goes to</span>
                            <select value={beneficiary} onChange={e => setBeneficiary(e.target.value)}
                                className={cn(input, "mt-1 w-full")}>
                                {BENEFICIARIES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs font-medium text-gray-600">Given as</span>
                            <select value={benefitType} onChange={e => setBenefitType(e.target.value)}
                                className={cn(input, "mt-1 w-full")}>
                                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs font-medium text-gray-600">Percentage</span>
                            <input
                                value={percent} inputMode="decimal"
                                onChange={e => setPercent(e.target.value.replace(/[^\d.]/g, ""))}
                                placeholder={suggestedPct != null
                                    ? `Configured default: ${suggestedPct}%`
                                    : "Optional — or type an amount instead"}
                                className={cn(input, "mt-1 w-full")}
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs font-medium text-gray-600">Amount</span>
                            <input
                                value={amount} inputMode="decimal"
                                onChange={e => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                                placeholder={computed != null
                                    ? `From the percentage: ${money(computed)}`
                                    : "The agreed figure"}
                                className={cn(input, "mt-1 w-full")}
                            />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-xs font-medium text-gray-600">
                                What is being given
                            </span>
                            <textarea
                                rows={3} value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="e.g. Two free modules (LIMS, Advanced BI) for 2026-27, in place of a cash payout."
                                className={cn(input, "mt-1 w-full")}
                            />
                        </label>
                    </div>

                    <div className="px-4 pb-4 flex items-center justify-between">
                        <span className="text-sm text-gray-700">
                            Recording <strong>{money(effective)}</strong>
                            {amount !== "" && percent && " — typed amount overrides the percentage"}
                        </span>
                        <button
                            type="button" disabled={!canAward} onClick={award}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm
                                font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            {saving ? "Recording…" : "Record reward"}
                        </button>
                    </div>
                </section>
            )}

            {/* ── Rewards ─────────────────────────────────────────────── */}
            <section className="rounded-lg border border-gray-200 bg-white">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
                    <h2 className="text-sm font-semibold text-gray-900 mr-2">
                        Rewards ({rewards.length})
                    </h2>
                    {STATUSES.map(s => (
                        <button
                            key={s.value} type="button" onClick={() => setStatusFilter(s.value)}
                            className={cn("px-3 py-1 rounded-full text-xs font-medium border",
                                statusFilter === s.value
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "bg-white text-gray-600 border-gray-200 hover:border-blue-400")}
                        >
                            {s.label}
                        </button>
                    ))}
                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                        className={cn(input, "ml-auto")}>
                        <option value="all">All forms</option>
                        {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>

                {loadingRewards && (
                    <div className="flex items-center justify-center py-10 text-gray-400">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                    </div>
                )}

                {!loadingRewards && rewards.length === 0 && (
                    <p className="px-4 py-8 text-center text-sm text-gray-500">
                        Nothing awarded yet.
                    </p>
                )}

                <div className="divide-y divide-gray-100">
                    {!loadingRewards && rewards.map(r => {
                        const type = TYPES.find(t => t.value === r.benefit_type)
                            || { label: r.benefit_type, Icon: Gift };
                        const TypeIcon = type.Icon;
                        return (
                            <div key={r.id} className={cn("px-4 py-3",
                                r.status === "cancelled" && "opacity-60")}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <TypeIcon className="w-4 h-4 text-gray-500 shrink-0" />
                                        <span className="text-sm font-medium text-gray-900">
                                            {r.customer_name || `Customer #${r.customer_id}`}
                                        </span>
                                        <span className="text-sm text-gray-900">{money(r.amount)}</span>
                                        <span className="text-xs text-gray-500">
                                            {r.beneficiary_type === "individual"
                                                ? `to ${r.beneficiary_name || "an individual"}`
                                                : "to the hospital"}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className={cn(
                                            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
                                            STATUS_CLS[r.status] || STATUS_CLS.draft)}>
                                            {r.status}
                                        </span>
                                        {/* Whether the referrer can see it yet. */}
                                        {r.status !== "draft" && (r.pushed_at
                                            ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                                            : <span title={r.push_error || "Not yet delivered to eMedHub"}>
                                                <XCircle className="w-4 h-4 text-amber-500" />
                                            </span>)}
                                    </div>
                                </div>

                                <p className="text-sm text-gray-700 mt-1.5 whitespace-pre-wrap">
                                    {r.description}
                                </p>

                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500">
                                    {r.percent != null && (
                                        <span>{r.percent}% of {money(r.basis_amount)}</span>
                                    )}
                                    <span className="font-mono">
                                        {(r.referrals || []).map(x => x.referral_code).join(", ")}
                                    </span>
                                    {r.approved_at && <span>Approved {formatDate(r.approved_at)}</span>}
                                    {r.settled_at && <span>Settled {formatDate(r.settled_at)}</span>}
                                    {r.awarded_by && <span>by {r.awarded_by}</span>}
                                </div>

                                {r.settlement_note && (
                                    <p className="mt-1.5 text-xs text-gray-600">
                                        <strong>Settled:</strong> {r.settlement_note}
                                    </p>
                                )}

                                {(r.status === "draft" || r.status === "approved") && (
                                    <div className="mt-2 flex items-center gap-3">
                                        {r.status === "draft" && (
                                            <button type="button" onClick={() => move(r.id, "approved")}
                                                className="text-xs text-blue-600 hover:underline">
                                                Approve
                                            </button>
                                        )}
                                        {r.status === "approved" && (
                                            <button type="button"
                                                onClick={() => { setSettling(r.id); setSettleNote(""); }}
                                                className="text-xs text-green-700 hover:underline">
                                                Settle
                                            </button>
                                        )}
                                        <button type="button" onClick={() => move(r.id, "cancelled")}
                                            className="text-xs text-red-600 hover:underline">
                                            Cancel
                                        </button>
                                    </div>
                                )}

                                {settling === r.id && (
                                    <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-3">
                                        <span className="text-xs font-medium text-gray-700">
                                            How was it settled?
                                        </span>
                                        <input
                                            value={settleNote}
                                            onChange={e => setSettleNote(e.target.value)}
                                            placeholder="Cheque no., modules enabled, or the AMC invoice it came off"
                                            className={cn(input, "mt-1 w-full")}
                                        />
                                        <div className="mt-2 flex gap-2">
                                            <button
                                                type="button" disabled={!settleNote.trim()}
                                                onClick={() => move(r.id, "settled", settleNote.trim())}
                                                className="px-3 py-1.5 rounded-lg text-xs font-medium
                                                    text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                            >
                                                Mark settled
                                            </button>
                                            <button type="button" onClick={() => setSettling(null)}
                                                className="px-3 py-1.5 rounded-lg text-xs text-gray-600 hover:underline">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
